import { useEffect, useState } from "react";
import DatePicker from "../DatePicker";
import { getDebtReport, getSuppliersDebt } from "../../utils/dataFetch";

export default function ReportModal({ toggleModal, selectedCounterparty }) {
    const today = new Date();
    const firstDayOfCurrentTheYear = new Date(new Date().getFullYear(), 0, 2);

    const [startDate, setStartDate] = useState(firstDayOfCurrentTheYear.toISOString().split("T")[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

    // Dashboard scroll lock when modal window is open
    useEffect(() => {
        document.body.classList.add("modal--open");
        return () => {
            document.body.classList.remove("modal--open");
        };
    }, []);

    function getReportData(data, initialDebt) {
        let startDebt = initialDebt;
        let currentDebt = initialDebt;
        let resultArray = [];
        let arrivalSum = 0;
        let paymentSum = 0;

        data.forEach((item) => {
            let docDate = new Date(item.date).toLocaleString();
            let arrival = item.arrival;
            let payment = item.payment;
            let endDebt = currentDebt + arrival - payment;

            resultArray.push({
                name: `${item.doc_label} №${item.doc_id} (${docDate})`,
                startDept: currentDebt.toFixed(2),
                arrival: arrival.toFixed(2),
                payment: payment.toFixed(2),
                endDept: endDebt.toFixed(2),
            });

            currentDebt = endDebt;
            arrivalSum += arrival;
            paymentSum += payment;
        });

        return {
            reportData: resultArray,
            overall: {
                startDept: startDebt.toFixed(2),
                arrivalSum: arrivalSum.toFixed(2),
                paymentSum: paymentSum.toFixed(2),
                endDept: currentDebt.toFixed(2),
            },
        };
    }

    const [dataError, setDataError] = useState("");
    const [debtReport, setDebtReport] = useState([]);
    const [overallDebtReport, setOverallDebtReport] = useState([]);

    useEffect(() => {
        const fetchDebtReport = async () => {
            try {
                const suppliersDeptResult = await getSuppliersDebt(startDate);
                const deptReportResult = await getDebtReport(startDate + "T0:0:0.0Z", endDate + "T23:59:59.999Z", selectedCounterparty.id);

                const findCurrentCounterpartyStartDept = suppliersDeptResult.list.find((item) => item.id === selectedCounterparty.id);

                const report = getReportData(deptReportResult, findCurrentCounterpartyStartDept ? findCurrentCounterpartyStartDept.debt : 0);
                setDebtReport(report.reportData);
                setOverallDebtReport(report.overall);
            } catch (error) {
                setDataError("Помилка завантаження даних!");
            }
        };
        fetchDebtReport();
    }, [startDate, endDate, selectedCounterparty.id]);

    return (
        <div className="report--debt modal--backdrop">
            <div className="modal d-block" tabIndex="-1">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content rounded-5">
                        <div className="modal-header">
                            <h5 className="modal-title ms-2">
                                Звіт <span className="fw-bold">{selectedCounterparty.name}</span>
                            </h5>
                            <button type="button" className="btn-close me-0" onClick={toggleModal}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3 me-2 me-sm-0">
                                <DatePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    onStartDateChange={setStartDate}
                                    onEndDateChange={setEndDate}
                                    minStartDate={firstDayOfCurrentTheYear}
                                    maxPeriodInDays={365}
                                />
                            </div>
                            {/* Table */}
                            {dataError && <div className="alert alert-danger rounded-5 m-auto">{dataError}</div>}
                            {!dataError && (
                                <>
                                    <div className="table-responsive border border-secondary rounded-4">
                                        <table className="table table-sm table-striped-columns table-light m-0">
                                            <thead>
                                                <tr>
                                                    <th className="text-center align-middle table-active" scope="col">
                                                        Документ
                                                    </th>
                                                    <th className="text-center align-middle table-active" scope="col">
                                                        Борг на початок
                                                    </th>
                                                    <th className="text-center align-middle table-active" scope="col">
                                                        Прихід
                                                    </th>
                                                    <th className="text-center align-middle table-active" scope="col">
                                                        Оплата
                                                    </th>
                                                    <th className="text-center align-middle table-active" scope="col">
                                                        Борг на кінець
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {debtReport.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="text-start align-start">{item.name}</td>
                                                        <td className={`text-center align-middle ${item.startDept < 0 && "text-danger"}`}>{item.startDept}</td>
                                                        <td className={`text-center align-middle ${item.arrival < 0 && "text-danger"}`}>
                                                            {Number(item.arrival) === 0 ? "" : item.arrival}
                                                        </td>
                                                        <td className={`text-center align-middle ${item.payment < 0 && "text-danger"}`}>
                                                            {Number(item.payment) === 0 ? "" : item.payment}
                                                        </td>
                                                        <td className={`text-center align-middle ${item.endDept < 0 && "text-danger"}`}>{item.endDept}</td>
                                                    </tr>
                                                ))}
                                                <tr>
                                                    <th scope="col" className="text-start align-start table-active">
                                                        Разом
                                                    </th>
                                                    <th scope="col" className="text-center align-middle table-active">
                                                        {overallDebtReport.startDept}
                                                    </th>
                                                    <th scope="col" className="text-center align-middle table-active">
                                                        {overallDebtReport.arrivalSum}
                                                    </th>
                                                    <th scope="col" className="text-center align-middle table-active">
                                                        {overallDebtReport.paymentSum}
                                                    </th>
                                                    <th scope="col" className="text-center align-middle table-active">
                                                        {overallDebtReport.endDept}
                                                    </th>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="row d-flex justify-content-center mt-3 mb-2 mb-sm-0">
                                        <button className="btn btn-sm main--button w-auto" onClick={toggleModal}>
                                            Закрити
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
